import { useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { writeTextToClipboard } from "@/utils/clipboard";

interface CopyFallbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  text: string;
}

export function CopyFallbackDialog({
  open,
  onOpenChange,
  text,
}: CopyFallbackDialogProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => {
      textareaRef.current?.focus();
      textareaRef.current?.select();
    }, 50);
    return () => window.clearTimeout(id);
  }, [open]);

  const handleRetry = async () => {
    try {
      await writeTextToClipboard(text);
      toast({
        title: "Copiado!",
        description: "Texto copiado para a área de transferência.",
      });
      onOpenChange(false);
    } catch {
      textareaRef.current?.focus();
      textareaRef.current?.select();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Copiar manualmente</DialogTitle>
          <DialogDescription>
            Não foi possível acessar a área de transferência automaticamente.
            Selecione o texto abaixo e copie com Ctrl/Cmd + C.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          ref={textareaRef}
          readOnly
          value={text}
          rows={Math.min(10, Math.max(4, text.split("\n").length + 1))}
          className="font-mono text-sm"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          <Button onClick={handleRetry}>Tentar copiar novamente</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
